import React from "react"
import PropTypes from "prop-types"
import SEO from "components/molecules/SEO"
import Layout from "components/molecules/Layout"
import { Information } from "components/molecules/PublicInformation"

const PrivacyPage = ({ pageContext: { language } }) => (
  <Layout language={language}>
    <SEO title="Privacy Notice" />
    <Information>
      <h1>Privacy Notice</h1>
      <p>Effective and last updated: October 13, 2026.</p>
      <p>
        Executive Development LLC (Exdev) provides this notice for its public
        website, general enquiries and project-update subscriptions. It explains
        what personal information we collect, why we use it, how it is disclosed
        and how to contact us about your rights. Separate notices may apply to
        employment, private investor onboarding or other services when provided.
      </p>
      <h2>Information we collect and why</h2>
      <ul>
        <li>
          <strong>Identifiers and contact information:</strong> name, email
          address, telephone number, mailing address or general residence
          information that you provide in correspondence or forms. We use these
          to respond, route enquiries and manage our business relationship.
        </li>
        <li>
          <strong>Correspondence and preferences:</strong> messages, selected
          projects, communication preferences and privacy requests. We use these
          to handle your requests, provide requested updates and maintain
          records of your choices.
        </li>
        <li>
          <strong>Internet and device information:</strong> IP address, browser
          and device details, requested pages and technical logs processed when
          the website and its media are delivered. We use these for delivery,
          troubleshooting and security. Optional Google content is described
          below.
        </li>
      </ul>
      <p>
        The sources are you, your browser or device, and service providers that
        operate the website and communications services. Do not provide Social
        Security numbers, bank credentials, identity documents or other
        sensitive information in a general enquiry. Our public enquiry channels
        do not require that information.
      </p>
      <h2>Cookies, browser storage and optional content</h2>
      <p>
        Our public website no longer includes Google Analytics or the Zendesk
        chat widget. Functional browser storage supports language preferences,
        navigation positions, banner dismissal and route suggestions. We store
        your optional-content choice in local storage for up to 180 days, or
        until you clear browser storage.
      </p>
      <p>
        Google Maps and YouTube embeds are blocked until you select Accept
        optional content. Acceptance lets your browser connect to Google, which
        receives information such as your IP address and may use cookies or
        other storage under its own policies. YouTube uses privacy-enhanced
        mode; playback is not necessarily free of cookies or data collection.
        Directly hosted videos are available without accepting optional content.
      </p>
      <p>
        Select Reject optional content to keep those embeds blocked. You can
        change or withdraw your choice using Privacy settings on any page.
        Rejecting after acceptance reloads the page to stop loaded embeds and
        scripts. Withdrawal does not delete information already received by
        Google or cookies on its domains. Manage those through your browser and
        Google settings.
      </p>
      <p>
        External links take you to a different website, whose practices apply.
        See{" "}
        <a href="https://policies.google.com/privacy">
          Google’s Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://www.google.com/help/terms_maps/">Google Maps Terms</a>.
      </p>
      <h2>Disclosure and service providers</h2>
      <p>
        We do not sell personal information or share it for cross-context
        behavioral advertising. We disclose information as needed to providers
        supporting website hosting, media delivery, communications, records and
        security. The public website uses Netlify for hosting and Amazon Web
        Services for media delivery. Google receives information when you choose
        to load its optional content or use a Google-hosted form.
      </p>
      <p>
        We may also disclose information as required by law or legal process, to
        protect rights and security, or in connection with a business transfer,
        subject to applicable law. Information may be processed in the United
        States and locations used by the relevant service providers.
      </p>
      <h2>Retention</h2>
      <p>
        We retain enquiry and correspondence records for the period needed to
        respond and manage the related relationship, taking account of legal
        obligations, disputes and record-keeping needs. Communication
        preferences are retained while needed to provide requested
        communications and honor opt-outs. Technical records are retained for
        operational and security needs. We assess retention by the purpose,
        sensitivity and legal requirements for each category, rather than
        retaining information indefinitely. The optional-content preference
        lasts up to 180 days.
      </p>
      <h2>Your California privacy rights</h2>
      <p>
        Subject to applicable law and exceptions, California residents may
        request to know and access the categories and specific pieces of
        personal information collected, its sources, purposes and recipients;
        request deletion; and request correction of inaccurate information. You
        have the right to equal treatment for exercising these rights.
      </p>
      <p>
        California law also provides rights to opt out of sale or sharing for
        cross-context behavioral advertising, and to limit certain uses and
        disclosures of sensitive personal information. Exdev does not sell or
        share personal information for those advertising purposes. Our public
        enquiry channels do not require sensitive personal information. Global
        Privacy Control is an opt-out signal for sale/sharing; our
        no-sale/no-sharing practice applies regardless of whether it is enabled.
        Browser Do Not Track is a separate signal and does not control optional
        embeds; use Privacy settings for those.
      </p>
      <h2>Submit a privacy request</h2>
      <p>
        Email{" "}
        <a href="mailto:info@exdev.com?subject=Privacy%20request">
          info@exdev.com
        </a>{" "}
        with the subject Privacy request, call{" "}
        <a href="tel:+16263845050">626-384-5050</a>, or write to Executive
        Development LLC, Attention: Privacy Requests, 17777 Center Court Dr. N
        #725, Cerritos, CA 90703. Describe the right you want to exercise and
        provide a way to reach you. Do not include passwords, Social Security
        numbers or full account numbers.
      </p>
      <p>
        We may request information reasonably necessary to verify your identity
        and authority before disclosing, correcting or deleting records. An
        authorized agent may submit a request; we may require proof of
        authorization and verification as permitted by law. We respond within
        applicable legal time limits and explain any applicable exception or
        permitted extension.
      </p>
      <p>
        California residents may also request information about certain
        disclosures for third parties’ direct marketing under California’s Shine
        the Light law by using the contact details above.
      </p>
      <h2>Communications and security</h2>
      <p>
        Use the unsubscribe link in a promotional email, or contact us, to
        change communication preferences. This may not stop necessary
        transactional or legal communications. If you receive text updates, you
        may request that they stop by contacting us. Privacy choices for this
        website are separate from communication preferences.
      </p>
      <p>
        No method of transmission or storage is completely secure. Use the
        contact details above to report a privacy concern, and avoid sending
        sensitive records through general enquiry channels.
      </p>
      <h2>Updates and contact</h2>
      <p>
        We will revise this notice when practices or requirements change and
        update the date above. Where required, we will provide additional notice
        before a material change. For questions or an accessible version of this
        notice, email <a href="mailto:info@exdev.com">info@exdev.com</a> or call{" "}
        <a href="tel:+16263845050">626-384-5050</a>.
      </p>
    </Information>
  </Layout>
)
PrivacyPage.propTypes = {
  pageContext: PropTypes.shape({ language: PropTypes.string }).isRequired,
}
export default PrivacyPage
